import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Box,
  Table,
  TableProps,
  Container,
  PropertyFilter,
  Pagination,
  Modal,
  FileUpload,
  Button as CloudscapeButton,
  Spinner,
  ProgressBar,
} from '@cloudscape-design/components';
import { Skill } from '@amzn/finspace-service-typescript-client';
import { Button } from '../common/Button';
import { TrajectoryLoader } from '../common/Loaders';
import { useSkillStore } from '../../store/skill-store';
import { usePropertyFilter, useTableSorting, createStandardComparator, useExperimentalMode } from '../../hooks';
import { PROPERTY_FILTER_I18N_STRINGS } from '../../constants/cloudscape-i18n';
import { ROUTES } from '../../constants';
import './skill-management-page.scss';

const PAGE_SIZE = 15;

export const SkillManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { experimentalModeEnabled } = useExperimentalMode();
  const {
    skills, isLoading, fetchSkills,
    modalVisible, selectedFile, s3Uri, isUploading, uploadProgress, isModalLoading, error,
    openCreateModal, closeModal, setSelectedFile, createSkill,
  } = useSkillStore();
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchSkills(experimentalModeEnabled);
  }, [experimentalModeEnabled, fetchSkills]);

  const {
    query: filteringQuery,
    setQuery: setFilteringQuery,
    filteredItems: filteredSkills,
    filteringProperties,
    filteringOptions,
  } = usePropertyFilter({
    items: skills,
    filterConfigs: [
      {
        key: 'name',
        propertyLabel: 'Skill Name',
        groupValuesLabel: 'Skill Name values',
        getValue: (skill: Skill) => skill.name,
      },
      {
        key: 'description',
        propertyLabel: 'Description',
        groupValuesLabel: 'Description values',
        getValue: (skill: Skill) => skill.description,
      },
    ],
  });

  const sortingComparator = useMemo(
    () => createStandardComparator<Skill>({
      name: (item) => item.name?.toLowerCase() || '',
      description: (item) => item.description?.toLowerCase() || '',
    }),
    []
  );

  const {
    sortedItems,
    sortingColumn,
    sortingDescending,
    handleSortingChange,
  } = useTableSorting({
    items: filteredSkills,
    defaultSortingField: 'name',
    defaultSortingDescending: false,
    sortingComparator,
  });

  const totalPages = Math.ceil(sortedItems.length / PAGE_SIZE);
  const paginatedSkills = sortedItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const columnDefinitions: TableProps.ColumnDefinition<Skill>[] = [
    {
      id: 'name',
      header: <Box fontWeight="bold">Skill Name</Box>,
      cell: (skill) => skill.name || '-',
      sortingField: 'name',
      width: '25%',
    },
    {
      id: 'description',
      header: <Box fontWeight="bold">Description</Box>,
      cell: (skill) => {
        const desc = skill.description || '-';
        return <span className="skill-description">{desc.length > 80 ? `${desc.substring(0, 80)}...` : desc}</span>;
      },
      sortingField: 'description',
      width: '45%',
    },
    {
      id: 'actions',
      header: <Box fontWeight="bold">Actions</Box>,
      cell: (skill) => (
        <Button
          variant="secondary"
          href={`/skill/${skill.skillId}/version/${skill.versionId}`}
        >
          View
        </Button>
      ),
      width: '30%',
    },
  ];

  if (isLoading && skills.length === 0) {
    return (
      <ContentLayout defaultPadding>
        <div className="loading-state">
          <TrajectoryLoader size="140px" speed="2s" />
          <div className="loading-message">Loading skills...</div>
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      defaultPadding
      header={
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="secondary" icon="arrow-left" onClick={() => navigate(ROUTES.DASHBOARD)}>
                Back
              </Button>
              <Button variant="primary" onClick={openCreateModal} disabled={isModalLoading}>
                {isModalLoading ? <Spinner size="normal" /> : 'Create Skill'}
              </Button>
            </SpaceBetween>
          }
        >
          Skill Management
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <Container>
          <div className="skill-filter-bar">
            <div className="skill-filter-bar__filter">
              <PropertyFilter
                query={filteringQuery}
                onChange={({ detail }) => { setFilteringQuery(detail); setCurrentPage(1); }}
                filteringProperties={filteringProperties}
                filteringOptions={filteringOptions}
                countText={`${sortedItems.length} ${sortedItems.length === 1 ? 'match' : 'matches'}`}
                i18nStrings={{
                  ...PROPERTY_FILTER_I18N_STRINGS,
                  filteringPlaceholder: 'Search by skill name or description',
                }}
              />
            </div>
            <Pagination
              currentPageIndex={currentPage}
              pagesCount={totalPages}
              onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
            />
          </div>
          <Table
            columnDefinitions={columnDefinitions}
            items={paginatedSkills}
            loading={isLoading}
            loadingText="Loading skills..."
            sortingColumn={sortingColumn}
            sortingDescending={sortingDescending}
            onSortingChange={handleSortingChange}
            empty={
              <Box textAlign="center" color="inherit">
                <b>No skills found</b>
                <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                  No skills have been uploaded yet.
                </Box>
              </Box>
            }
            variant="borderless"
          />
        </Container>
      </SpaceBetween>

      <Modal
        visible={modalVisible}
        onDismiss={closeModal}
        header="Create Skill"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <CloudscapeButton variant="link" onClick={closeModal} disabled={isUploading}>
                Cancel
              </CloudscapeButton>
              <CloudscapeButton
                variant="primary"
                disabled={selectedFile.length === 0 || !s3Uri || isUploading}
                onClick={createSkill}
              >
                {isUploading ? <Spinner size="normal" /> : 'Upload'}
              </CloudscapeButton>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FileUpload
            accept=".zip,application/zip"
            constraintText="Only ZIP files are allowed"
            value={selectedFile}
            onChange={({ detail }) => setSelectedFile(detail.value)}
            i18nStrings={{
              uploadButtonText: (multiple) => multiple ? 'Choose files' : 'Choose file',
              dropzoneText: (multiple) => multiple ? 'Drop files to upload' : 'Drop file to upload',
              removeFileAriaLabel: (fileIndex) => `Remove file ${fileIndex + 1}`,
              limitShowFewer: 'Show fewer files',
              limitShowMore: 'Show more files',
              errorIconAriaLabel: 'Error',
            }}
          />
          {isUploading && (
            <ProgressBar
              value={uploadProgress}
              label="Upload progress"
              description={`${Math.round(uploadProgress)}% complete`}
            />
          )}
          {error && modalVisible && (
            <Box color="text-status-error">{error}</Box>
          )}
        </SpaceBetween>
      </Modal>
    </ContentLayout>
  );
};
